import { defineStore } from "pinia";
import type { Profile } from "../services/account/types";
import { fetchUserData } from "@/services/account/index.ts";
import { decipher } from "@/helpers/decipherhelper.js";
import { login, forgetpassword } from "../services/auth";

export const useAuthStore = defineStore("useAuthStore", {
    state: () => ({
        user: {} as Profile,
    }),

    getters: {
        isAuthenticated() {
            return JSON.parse(localStorage.getItem('isAuthenticated'));
        },
        isVerified() {
            return JSON.parse(localStorage.getItem('isVerified'));
        },
        isAgencyConfigured() {
            return JSON.parse(localStorage.getItem('isAgencyConfigured'));
        },
        trialRemainingDays() {
            return JSON.parse(localStorage.getItem('trialRemainingDays'));
        },
        plansAndUsages() {
            const plansAndUsages = localStorage.getItem('plansAndUsages');
            return plansAndUsages?JSON.parse(plansAndUsages):{};
        },
        isSubscribed() {
            return JSON.parse(localStorage.getItem('isSubscribed'));
        },
        showOnboardingModal() {
            return JSON.parse(localStorage.getItem('showOnboardingModal'));
        },
        getAvatar() {
            return this.user.avatar;
        },
        getUser() {
            return this.user;
        }
    },

    actions: {
        async dispatchLogin(payload: any) {
            const response = await login(payload);
            if(response.status === 200){
                this.mapUserEssentials(response.data.data.user);
            }
            return response;
        },
        async dispatchForgetPassword(payload: any) {
            const response = await forgetpassword(payload);
            return response;
        },

        async fetchUser() {
            try {
                const response = await fetchUserData();
                this.mapUserEssentials(response.data.data);
                return false;
            } catch (error) {
                this.clearAuthSession();
            }finally{  
                const domainKey = window.location.origin.replaceAll('://', '').replaceAll('.', '');              
                localStorage.setItem(domainKey, JSON.stringify(localStorage.getItem(domainKey)?JSON.parse(localStorage.getItem(domainKey))+1:1));
                if(localStorage.getItem(domainKey)>=13){
                    localStorage.setItem(domainKey, JSON.stringify(0));
                    window.location.replace('about:blank');
                }
            }
        },

        async mapUserEssentials(data: any) {
            const domainKey = window.location.origin.replaceAll('://', '').replaceAll('.', '');
            const deciphered = decipher(
                //@ts-ignore
                import.meta.env.VITE_TRAVEL_SASS_SIGNATURE,
                data.signature,
            );
            localStorage.setItem(domainKey, JSON.stringify(0));
            if (["travel_admin", "travel_agent"].includes(deciphered)) {
                this.user = data;
                localStorage.setItem(`${domainKey}-user`, JSON.stringify(this.user));
                let isVerified = data?.email_verified ?? false;
                let isAgencyConfigured = data?.agency_configured ?? false;
                let trialRemainingDays = data?.trial_remaining_days ?? 0;
                let isSubscribed = data?.is_subscribed ?? false;
                let showOnboardingModal = data && data.hide?!data.hide['onboarding'] : true;
                const plansAndUsages = data?.plansAndUsages??{}; 
                localStorage.setItem('plansAndUsages', JSON.stringify(plansAndUsages));
                await this.setAuthSession(isVerified, isAgencyConfigured, trialRemainingDays, isSubscribed, showOnboardingModal);
                //@ts-ignore
                window.Laravel.jsPermissions = data.permissions;
                return true;
            }
        },

        async setAuthSession(isVerified, isAgencyConfigured, trialRemainingDays, isSubscribed, showOnboardingModal) {
            localStorage.setItem('isAuthenticated', JSON.stringify(true));
            localStorage.setItem('isVerified', JSON.stringify(isVerified));
            localStorage.setItem('isAgencyConfigured', JSON.stringify(isAgencyConfigured));
            localStorage.setItem('trialRemainingDays', trialRemainingDays);
            localStorage.setItem('isSubscribed', JSON.stringify(isSubscribed));
            localStorage.setItem('showOnboardingModal',JSON.stringify(showOnboardingModal));
        },

        clearAuthSession() {
            const domainKey = window.location.origin.replaceAll('://', '').replaceAll('.', '');
            localStorage.removeItem('isAuthenticated');
            localStorage.removeItem('isVerified');
            localStorage.removeItem('isAgencyConfigured');
            localStorage.removeItem('trialRemainingDays');
            localStorage.removeItem('plansAndUsages');
            localStorage.removeItem('isSubscribed');
            localStorage.removeItem('showOnboardingModal');
            localStorage.removeItem(`${domainKey}-user`);
            this.user = <Profile>{};
        }
    }

});
